'use client'
import { useState, useEffect } from 'react'
import Editor from '@monaco-editor/react'
import { Panel, Group, Separator } from 'react-resizable-panels'

interface SandboxProps {
  initialData?: {
    id: string
    title: string
    html: string
    css: string
    js: string
    htmlPath?: string
    cssPath?: string
    jsPath?: string
  } | null
}

type LogEntry = {
  id: string
  type: 'log' | 'warn' | 'error'
  message: string
}

export default function LiveCodeSandbox({ initialData }: SandboxProps) {
  // 1. Separate the default templates
  const defaultHtml = `<div class="card">\n  <h1>✨ Hello World</h1>\n  <p>Edit HTML, CSS, or JS and watch it render live!</p>\n  <button id="btn">Click Me</button>\n</div>`
  const defaultCss = `body {\n  background: #090d1e;\n  font-family: monospace;\n  display: flex;\n  justify-content: center;\n  align-items: center;\n  height: 100vh;\n  margin: 0;\n  color: #f8fafc;\n}\n.card {\n  background: #0f172a;\n  border: 1px solid #334155;\n  padding: 2rem;\n  border-radius: 1.5rem;\n  text-align: center;\n  box-shadow: 0 20px 25px -5px rgb(0 0 0 / 0.5);\n}\nh1 { color: #818cf8; margin-top: 0; }\nbutton {\n  background: #6366f1;\n  color: white;\n  border: none;\n  padding: 0.75rem 1.5rem;\n  font-weight: bold;\n  border-radius: 0.75rem;\n  cursor: pointer;\n  margin-top: 1rem;\n  transition: 0.2s;\n}\nbutton:hover { background: #4f46e5; }`
  const defaultJs = `const btn = document.getElementById('btn');\n\nbtn.addEventListener('click', () => {\n  console.log('🚀 Button clicked!');\n  console.warn('This is a test warning.');\n  \n  // Uncomment below to test an error!\n  // throw new Error("Oops, something broke!");\n});\n\nconsole.log("Sandbox initialized successfully.");`

  // 2. Initialize State (Checking if initialData has the content, otherwise using defaults)
  const [scriptId, setScriptId] = useState(initialData?.id || null)
  const [scriptTitle, setScriptTitle] = useState(initialData?.title || 'My Awesome Pen')
  const [html, setHtml] = useState(initialData?.html ?? defaultHtml)
  const [css, setCss] = useState(initialData?.css ?? defaultCss)
  const [js, setJs] = useState(initialData?.js ?? defaultJs)

  // 3. FORCE State Sync (Bulletproof override for any prop changes)
  useEffect(() => {
    setScriptId(initialData?.id || null)
    setScriptTitle(initialData?.title || 'My Awesome Pen')
    setHtml(initialData?.html ?? defaultHtml)
    setCss(initialData?.css ?? defaultCss)
    setJs(initialData?.js ?? defaultJs)
  }, [initialData])

  const [activeTab, setActiveTab] = useState<'html' | 'css' | 'js' | 'console'>('html')
  const [autoRun, setAutoRun] = useState<boolean>(true)
  const [srcDoc, setSrcDoc] = useState<string>('')
  
  const [isSaving, setIsSaving] = useState(false)
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle')
  const [logs, setLogs] = useState<LogEntry[]>([])

  // Intercept messages from the iframe sandbox
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data && event.data.source === 'sandbox-console') {
        setLogs(prev => [...prev, {
          id: Math.random().toString(36).substring(7),
          type: event.data.type,
          message: event.data.message
        }])
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [])

  // Compile output for iframe preview and inject the console interceptor
  useEffect(() => {
    const timeout = setTimeout(() => {
      setLogs([]) // Clear console on new run
      
      const consoleInterceptor = `
        <script>
          (function() {
            const originalLog = console.log;
            const originalWarn = console.warn;
            const originalError = console.error;

            function sendToParent(type, args) {
              try {
                const message = Array.from(args).map(arg => 
                  typeof arg === 'object' ? JSON.stringify(arg) : String(arg)
                ).join(' ');
                window.parent.postMessage({ source: 'sandbox-console', type, message }, '*');
              } catch(e) {
                window.parent.postMessage({ source: 'sandbox-console', type, message: '[Unserializable Object]' }, '*');
              }
            }

            console.log = function(...args) {
              sendToParent('log', args);
              originalLog.apply(console, args);
            };
            console.warn = function(...args) {
              sendToParent('warn', args);
              originalWarn.apply(console, args);
            };
            console.error = function(...args) {
              sendToParent('error', args);
              originalError.apply(console, args);
            };
            
            window.onerror = function(msg, url, line, col, error) {
              window.parent.postMessage({ source: 'sandbox-console', type: 'error', message: msg }, '*');
              return false;
            };
          })();
        </script>
      `

      setSrcDoc(`
        <!DOCTYPE html>
        <html>
          <head>
            ${consoleInterceptor}
            <style>${css}</style>
          </head>
          <body>
            ${html}
            <script>
              try {
                ${js}
              } catch (err) {
                console.error(err);
              }
            </script>
          </body>
        </html>
      `)
    }, 250)

    return () => clearTimeout(timeout)
  }, [html, css, js])

  const handleSaveToVault = async () => {
    if (!scriptTitle.trim()) {
      alert('Please enter a title before saving.')
      return
    }

    setIsSaving(true)
    setSaveStatus('idle')

    try {
      const response = await fetch('/api/vault', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scriptId, title: scriptTitle, html, css, js })
      })

      if (!response.ok) throw new Error('Network response was not ok')
      
      const data = await response.json()
      
      // If it created a new script, redirect to the edit URL silently
      if (data.action === 'created' && data.script?.id) {
        window.history.replaceState(null, '', `/practice/${data.script.id}`)
      }
      
      setSaveStatus('success')
      setTimeout(() => setSaveStatus('idle'), 3000)
    } catch (error) {
      console.error('Save failed', error)
      setSaveStatus('error')
      setTimeout(() => setSaveStatus('idle'), 3000)
    } finally {
      setIsSaving(false)
    }
  }

  const handleEditorChange = (value: string | undefined) => {
    const safeValue = value || ''
    if (activeTab === 'html') setHtml(safeValue)
    else if (activeTab === 'css') setCss(safeValue)
    else if (activeTab === 'js') setJs(safeValue)
  }

  return (
    <div className="bg-slate-950 border border-slate-800 rounded-3xl overflow-hidden shadow-2xl font-mono text-xs flex flex-col">
      {/* Sandbox Header / Toolbar */}
      <div className="bg-slate-900 border-b border-slate-800 p-4 flex flex-col sm:flex-row items-center justify-between gap-4 z-10 relative">
        <div className="flex items-center gap-3 w-full sm:w-auto">
          <div className="hidden sm:flex items-center gap-1.5 mr-2">
            <span className="w-3 h-3 rounded-full bg-rose-500"></span>
            <span className="w-3 h-3 rounded-full bg-amber-500"></span>
            <span className="w-3 h-3 rounded-full bg-emerald-500"></span>
          </div>
          <input 
            type="text" 
            value={scriptTitle}
            onChange={(e) => setScriptTitle(e.target.value)}
            placeholder="Name your script..."
            className="bg-slate-950 border border-slate-700 text-white font-bold px-3 py-1.5 rounded-lg focus:outline-none focus:border-indigo-500 w-full sm:w-48"
          />
          <button 
            onClick={handleSaveToVault}
            disabled={isSaving}
            className="bg-emerald-600/20 hover:bg-emerald-600/30 text-emerald-400 border border-emerald-500/30 font-bold px-3 py-1.5 rounded-lg transition-all flex items-center gap-2 whitespace-nowrap disabled:opacity-50"
          >
            {isSaving ? 'Saving...' : saveStatus === 'success' ? '✓ Saved!' : '💾 Save to Vault'}
          </button>
        </div>

        <div className="flex items-center justify-between w-full sm:w-auto gap-4">
          <label className="flex items-center gap-2 text-slate-400 cursor-pointer">
            <input 
              type="checkbox" 
              checked={autoRun} 
              onChange={(e) => setAutoRun(e.target.checked)} 
              className="rounded bg-slate-950 border-slate-700 text-indigo-600 focus:ring-0"
            />
            Auto-run
          </label>
          <button 
            onClick={() => {
              const current = srcDoc
              setSrcDoc('')
              setTimeout(() => setSrcDoc(current), 50)
            }}
            className="bg-indigo-600 hover:bg-indigo-500 text-white font-bold px-4 py-1.5 rounded-xl transition-all shadow-lg"
          >
            ▶ Run
          </button>
        </div>
      </div>

      {/* Main Split View with Draggable Panels (v4 API) */}
      <div className="h-[600px] w-full flex">
        <Group orientation="horizontal">
          
          {/* Left: Code Editors & Console */}
          <Panel defaultSize="50%" minSize="20%" className="flex flex-col bg-[#1e1e1e]">
            <div className="flex bg-slate-900/50 border-b border-slate-800 overflow-x-auto no-scrollbar">
              {/* Dynamic File Path Tabs */}
              <button
                onClick={() => setActiveTab('html')}
                className={`px-5 py-3 font-bold border-b-2 transition-all shrink-0 ${activeTab === 'html' ? 'border-indigo-500 text-indigo-400 bg-[#1e1e1e]' : 'border-transparent text-slate-400 hover:text-slate-200 bg-slate-900/50'}`}
              >
                {initialData?.htmlPath || 'index.html'}
              </button>
              <button
                onClick={() => setActiveTab('css')}
                className={`px-5 py-3 font-bold border-b-2 transition-all shrink-0 ${activeTab === 'css' ? 'border-indigo-500 text-indigo-400 bg-[#1e1e1e]' : 'border-transparent text-slate-400 hover:text-slate-200 bg-slate-900/50'}`}
              >
                {initialData?.cssPath || 'styles.css'}
              </button>
              <button
                onClick={() => setActiveTab('js')}
                className={`px-5 py-3 font-bold border-b-2 transition-all shrink-0 ${activeTab === 'js' ? 'border-indigo-500 text-indigo-400 bg-[#1e1e1e]' : 'border-transparent text-slate-400 hover:text-slate-200 bg-slate-900/50'}`}
              >
                {initialData?.jsPath || 'script.js'}
              </button>
              <button
                onClick={() => setActiveTab('console')}
                className={`px-5 py-3 font-bold border-b-2 transition-all shrink-0 flex items-center gap-2 ${activeTab === 'console' ? 'border-indigo-500 text-indigo-400 bg-[#1e1e1e]' : 'border-transparent text-slate-400 hover:text-slate-200 bg-slate-900/50'}`}
              >
                Console 
                {logs.length > 0 && (
                  <span className="bg-indigo-500 text-white text-[9px] px-1.5 py-0.5 rounded-full">{logs.length}</span>
                )}
              </button>
            </div>

            {/* Render Monaco OR Console based on active tab */}
            <div className="flex-1 w-full h-full relative">
              {activeTab !== 'console' ? (
                <div className="flex flex-col w-full h-full pt-2">
                  <div className="flex items-center justify-between px-3 py-1 mb-1">
                    <span className="text-[11px] font-mono text-slate-400 bg-slate-900 px-2 py-0.5 rounded border border-slate-800">
                      {activeTab === 'html' ? (initialData?.htmlPath || 'index.html') : activeTab === 'css' ? (initialData?.cssPath || 'styles.css') : (initialData?.jsPath || 'script.js')}
                    </span>
                  </div>
                  <div className="flex-1">
                    <Editor
                      height="100%"
                      theme="vs-dark"
                      language={
                        activeTab === 'html' ? 'html' : 
                        activeTab === 'css' ? 'css' : 
                        initialData?.jsPath?.endsWith('.ts') ? 'typescript' : 'javascript'
                      }
                      value={activeTab === 'html' ? html : activeTab === 'css' ? css : js}
                      onChange={handleEditorChange}
                      options={{
                        minimap: { enabled: false },
                        fontSize: 13,
                        wordWrap: 'on',
                        scrollBeyondLastLine: false,
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                        padding: { top: 8 },
                        smoothScrolling: true,
                        cursorBlinking: 'smooth',
                        formatOnPaste: true,
                      }}
                    />
                  </div>
                </div>
              ) : (
                <div className="absolute inset-0 bg-[#1e1e1e] p-4 overflow-y-auto flex flex-col space-y-2">
                  <div className="flex justify-between items-center pb-2 border-b border-slate-700/50 mb-2">
                    <span className="text-slate-400 uppercase tracking-widest text-[10px] font-bold">Terminal Output</span>
                    <button 
                      onClick={() => setLogs([])}
                      className="text-rose-400 hover:text-rose-300 text-xs transition-colors"
                    >
                      Clear 🚫
                    </button>
                  </div>
                  
                  {logs.length === 0 ? (
                    <div className="text-slate-600 italic mt-4 flex items-center gap-2">
                      <span className="text-lg">›</span> Waiting for output...
                    </div>
                  ) : (
                    logs.map(log => (
                      <div 
                        key={log.id} 
                        className={`p-3 rounded-lg border-l-2 font-mono whitespace-pre-wrap break-words ${
                          log.type === 'error' ? 'border-rose-500 bg-rose-500/10 text-rose-300' : 
                          log.type === 'warn' ? 'border-amber-500 bg-amber-500/10 text-amber-300' : 
                          'border-indigo-500 bg-slate-800/40 text-slate-300'
                        }`}
                      >
                        {log.message}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </Panel>

          {/* Draggable Divider */}
          <Separator className="w-2 bg-slate-900 border-x border-slate-800 flex items-center justify-center cursor-col-resize hover:bg-indigo-900/50 transition-colors group relative z-20">
            <div className="w-0.5 h-8 bg-slate-600 rounded-full group-hover:bg-indigo-400 transition-colors"></div>
          </Separator>

          {/* Right: Live Output Preview */}
          <Panel defaultSize="50%" minSize="20%" className="bg-white flex flex-col relative z-10">
            <div className="bg-slate-900 text-slate-400 px-4 py-2 border-b border-slate-800 text-[10px] font-bold uppercase tracking-wider flex items-center justify-between shrink-0">
              <span>📺 Live Output</span>
              <span className="text-emerald-400">● Sandboxed</span>
            </div>
            <iframe
              srcDoc={autoRun ? srcDoc : undefined}
              title="output"
              sandbox="allow-scripts allow-modals"
              className="w-full h-full border-none bg-white flex-1"
            />
          </Panel>

        </Group>
      </div>
    </div>
  )
}